<script setup lang="ts">
import type { z } from 'zod';

import { useMutation } from '@urql/vue';

import { useAccountEligibility } from '~/composables/unified/useAccountEligibility';
import { useMarketingPreference } from '~/composables/unified/useMarketingPreference';
import { useProfileForm } from '~/composables/unified/useProfileForm';
import { useUIVerification } from '~/composables/Verification/useUIVerification';
import { updateStudentRoleMutation } from '~/graphql/documents/core';
import { getFirstErrorMessageFromCombinedErrors } from '~/utils/graphql';

/**
 * The student's profile form. `withMarketingEmails` offers the marketing emails choice (only the
 * account page does); `hideAnimations` leaves out the animations checkbox, for a page that offers
 * that setting on its own.
 */
interface Props {
  withMarketingEmails?: boolean;
  hideAnimations?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{ verified: []; saved: []; saving: [saving: boolean] }>();

const { t } = useI18n();
const { accountStudentRole } = useAuth();
const authStore = useAuthStore();
const { show: showToast } = useToast();
const accessibilityStore = useAccessibilityStore();
const analytics = useAnalyticsService();
const { isPhoneVerified } = useUIVerification();
const { isAccountComplete } = useAccountEligibility();

const handleVerified = async () => {
  isPhoneVerified.value = true;
  await authStore.fetchUser(true);
  emit('verified');
};

const marketingPreference = await useMarketingPreference(props.withMarketingEmails);

const { formConfig } = await useProfileForm({
  onVerified: handleVerified,
  marketingOptedIn: marketingPreference.optedIn.value,
  withAnimations: !props.hideAnimations,
});

const isSaving = ref(false);
// Reported as it changes, not on the next tick, so a host never acts on a stale "still saving".
const setSaving = (saving: boolean) => {
  isSaving.value = saving;
  emit('saving', saving);
};
const formRef = useTemplateRef<{ setFieldValue: (key: string, value: boolean) => void }>('formRef');

const { executeMutation: _updateStudentRole } = useMutation(updateStudentRoleMutation);

const handleSave = async (value: z.infer<typeof formConfig.schema>) => {
  if (!accountStudentRole.value) {
    showToast({ variant: 'error', message: t('general.errors.unknown') });
    return;
  }

  setSaving(true);

  const wasAccountComplete = isAccountComplete.value;
  const animationsEnabled = 'animationsEnabled' in value ? value.animationsEnabled : undefined;
  const animationsToggled = typeof animationsEnabled === 'boolean' && animationsEnabled !== accessibilityStore.animationsEnabled;
  const marketingEmails = 'marketingEmails' in value ? value.marketingEmails : undefined;
  const marketingToggled = typeof marketingEmails === 'boolean' && marketingEmails !== marketingPreference.optedIn.value;

  const { data, error } = await _updateStudentRole({
    input: {
      id: accountStudentRole.value.id,
      firstName: value.firstName,
      lastName: value.lastName,
      stateId: value.stateId,
      ...('birthDate' in value ? { birthDate: value.birthDate } : {}),
      gender: value.gender,
    },
  });

  if (!data?.updateStudentRole?.studentRole) {
    showToast({ variant: 'error', message: error ? getFirstErrorMessageFromCombinedErrors(t, error, t('general.errors.unknown')) : t('general.errors.unknown') });
    setSaving(false);
    return;
  }

  // The profile has saved by now, so a failed preference save is reported without holding back
  // the completion and saved events that depend on it.
  let preferenceFailed = false;

  if (marketingToggled) {
    const marketingError = await marketingPreference.save(marketingEmails);
    if (marketingError) {
      preferenceFailed = true;
      formRef.value?.setFieldValue('marketingEmails', !marketingEmails);
      showToast({ variant: 'error', message: getFirstErrorMessageFromCombinedErrors(t, marketingError, t('general.errors.unknown')) });
    }
  }

  if (animationsToggled) {
    const animationsError = await accessibilityStore.setAnimationSetting(animationsEnabled);
    if (animationsError) {
      preferenceFailed = true;
      formRef.value?.setFieldValue('animationsEnabled', accessibilityStore.animationsEnabled);
      showToast({ variant: 'error', message: getFirstErrorMessageFromCombinedErrors(t, animationsError, t('general.errors.unknown')) });
    }
    else {
      analytics.track(EVENTS.ACCESSIBILITY_SETTINGS_UPDATED);
    }
  }

  setSaving(false);
  if (!preferenceFailed) {
    showToast({ variant: 'success', message: t('general.profile.updatedMessage') });
  }

  if (!wasAccountComplete && isAccountComplete.value) {
    analytics.track(EVENTS.UNIFIED_PROFILE_DETAILS_COMPLETED);
    emit('verified');
  }

  emit('saved');
};
</script>

<template>
  <KUICoreForm
    ref="formRef"
    :form-config="formConfig"
    :loading="isSaving"
    button-variant="default"
    @submit="handleSave"
  />
</template>
